import ExcelJS from 'exceljs';
import {saveDataToFile} from "./exportDocSheet";

const formatter = new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
});
const formatter_month = new Intl.DateTimeFormat('de-DE', {
    month: 'long',
});

/**
 * Helper function to export signature lists as excel sheet
 * @param plan plan for which the signature list of the participant(s) will be exported
 * @returns {Promise<void>} returns signature list as xlsx file
 */
export async function exportSignatureList(plan) {
    const buffer = (plan.group === undefined || plan.group === null)
        ? await generateSingleSignatureList(plan)
        : await generateGroupSignatureList(plan);

    const date = new Date(plan.date);
    const yy = String(date.getFullYear()).slice(-2);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${yy}${mm}${dd}`;
    const name = (plan.group === undefined || plan.group === null) ? plan.participant.lastname : plan.group.name;
    const filename = `${formattedDate}_Unterschriftenblatt_${name}_${plan.user.lastname}_${plan.topic.name}.xlsx`

    saveDataToFile(
        buffer,
        filename,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );
}

/**
 * Helper function for creating a workbook with a worksheet from a template
 * @param path path to the template
 * @returns {Promise<(ExcelJS.Workbook|*)[]>}
 */
async function getWorkbookAndWorksheet(path) {
    const blob = await (await fetch(path)).blob();
    const buffer = await blob.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    return [workbook, workbook.worksheets[0]];
}

/**
 * Helper function to get all training dates
 * @param planDate date of a plan of which all other dates are based on
 * @returns {dates: Date[]}
 */
function getDates(planDate){
    const dates = []
    const date = new Date(planDate);

    // find first training date of the month
    while(date.getDate() > 7){
        date.setDate(date.getDate()-7);
    }

    // add all training dates
    const month = date.getMonth();
    do{
        dates.push(new Date(date.getTime()));
        date.setDate(date.getDate() + 7);
    }
    while(date.getMonth() === month);
    return dates;
}

/**
 * Helper function to generate excel signature file for a group
 * @param plan plan to generate signature list of
 * @returns {buffer: Uint8Array, name: string} buffer with the excel file content
 */
export async function generateGroupSignatureList(plan) {
    const [workbook, worksheet] = await getWorkbookAndWorksheet("template_signature_group.xlsx");

    worksheet.getCell('F2').value = formatter_month.format(new Date(plan.date));
    worksheet.getCell('B2').value = plan.group.name;
    worksheet.getCell('B3').value = plan.group.serviceCenter.name;
    worksheet.getCell('D2').value = plan.user.firstname + " " + plan.user.lastname;

    const dates = getDates(plan.date);
    let colNum = 'B'.charCodeAt(0);
    for (let i = 0; i < dates.length; i++) {
        let colLetter = String.fromCharCode(colNum+i);
        worksheet.getCell(colLetter+"4").value = formatter.format(dates[i]);
    }

    const participant_row_index = 5;
    worksheet.duplicateRow(participant_row_index, plan.group.participants.length - 1, true);
    for (const [i, participant] of plan.group.participants.entries()) {
        worksheet.getCell('A' + (participant_row_index + i)).value = participant.firstname + " " + participant.lastname;
    }

    return await workbook.xlsx.writeBuffer();
}


/**
 * Helper function to generate excel signature file for one participant
 * @param plan plan to generate signature list of
 * @returns {buffer: Uint8Array, name: string} buffer with the excel file content
 */
export async function generateSingleSignatureList(plan) {
    const [workbook, worksheet] = await getWorkbookAndWorksheet("template_signature_single.xlsx");
    const allDates = getDates(plan.date);
    const date = new Date(plan.date);

    const formatted = new Intl.DateTimeFormat('de-DE', {
        month: '2-digit',
        year: 'numeric'
    }).format(date);

    worksheet.getCell('C2').value = plan.participant.serviceCenter.name;
    worksheet.getCell('C3').value = plan.user.firstname + " " + plan.user.lastname;
    worksheet.getCell('C4').value = formatted;
    worksheet.getCell('A7').value = formatter.format(date);
    for (let i = 0; i < allDates.length; i++) {
        worksheet.getCell('A' + (7 + i)).value = allDates[i];
        worksheet.getCell('B' + (7 + i)).value = plan.participant.firstname + ' ' + plan.participant.lastname;
    }
    return await workbook.xlsx.writeBuffer();
}